import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ModalShell from "../ModalShell";
import Button from "../TailwindComponents/Button";
import Badge from "../TailwindComponents/Badge";
import SearchInput from "../TailwindComponents/SearchInput";
import ProfileImage from "../../Header/ProfileImage";
import {
  PiUsersLight,
  PiMinusLight,
  PiPlusLight,
} from "react-icons/pi";
import { OrgMember } from "../../../store/actions/PlanActions";
import { normalise } from '../access/accessModel';

/** Format snake_case role strings → Title Case */
const formatRole = (role: string): string =>
  role
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

interface AddSeatsModalProps {
  open: boolean;
  onClose: () => void;
  currentSeats: number;
  seatsUsed: number;
  onConfirm: (newQuantity: number) => Promise<void>;
  perSeatPrice?: number;
  billingPeriod?: string;
  members?: OrgMember[];
  priceCurrency?: string | null;
}

const AddSeatsModal: React.FC<AddSeatsModalProps> = ({
  open,
  onClose,
  currentSeats,
  seatsUsed,
  onConfirm,
  perSeatPrice,
  billingPeriod,
  members = [],
  priceCurrency,
}) => {
  const { t, i18n } = useTranslation();
  const formatPrice = (amount: number) =>
    new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: priceCurrency || "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  const [newQuantity, setNewQuantity] = useState(currentSeats);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  React.useEffect(() => {
    if (open) {
      setNewQuantity(currentSeats);
      setSearchQuery("");
    }
  }, [open, currentSeats]);

  const delta = newQuantity - currentSeats;
  const belowUsed = newQuantity < seatsUsed;
  const usagePercent = currentSeats > 0 ? Math.min(100, (seatsUsed / currentSeats) * 100) : 0;

  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return members;
    const q = searchQuery.toLowerCase();
    return members.filter(
      (m) =>
        (m.name && m.name.toLowerCase().includes(q)) ||
        (m.email && m.email.toLowerCase().includes(q)) ||
        (m.role && formatRole(m.role).toLowerCase().includes(q))
    );
  }, [members, searchQuery]);

  const handleConfirm = async () => {
    if (delta === 0) return;
    setLoading(true);
    try {
      await onConfirm(newQuantity);
      onClose();
    } catch {
      // Error feedback already shown by the action - keep modal open
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell
      isOpen={open}
      onClose={onClose}
      title={t("Manage seats")}
      icon={<PiUsersLight />}
      size="lg"
      bodyClassName="flex flex-col overflow-hidden"
      footer={
        <div className="flex w-full items-center justify-between">
          <Button variant="neutral" size="small" onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleConfirm}
            disabled={delta === 0 || (belowUsed)}
            isLoading={loading}
          >
            {delta > 0 ? t("Add seats") : delta < 0 ? t("Remove seats") : t("No change")}
          </Button>
        </div>
      }
    >
              {/* Seat overview section */}
              <div className="px-5 pt-5 pb-4 space-y-4 border-b border-hairline">
                {/* Visual usage bar */}
                <div>
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="text-fg-muted">{t("Seat usage")}</span>
                    <span className="text-fg-strong font-medium">
                      {seatsUsed} / {currentSeats} {t("occupied")}
                    </span>
                  </div>
                  <div className="w-full bg-surface-sunken rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        usagePercent >= 100
                          ? "bg-danger-500"
                          : usagePercent >= 75
                          ? "bg-warning-500"
                          : "bg-primary-500"
                      }`}
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1.5">
                    <span className="text-fg-muted">
                      {currentSeats - seatsUsed} {t("available")}
                    </span>
                    {perSeatPrice != null && (
                      <span className="text-fg-muted">
                        {formatPrice(perSeatPrice)}/{t("seat")}/{billingPeriod === "annual" ? t("mo") + " " + t("billed annually") : t("mo")}
                      </span>
                    )}
                  </div>
                </div>

                {/* Seat stepper */}
                <div className="flex items-center justify-between bg-surface-sunken/70 rounded-lg p-4">
                  <div>
                    <p className="text-sm font-medium text-fg-strong">{t("Total seats")}</p>
                    <p className="text-xs text-fg-muted mt-0.5">{t("Adjust the number of seats for your organization")}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setNewQuantity(Math.max(1, newQuantity - 1))}
                      disabled={newQuantity <= 1}
                      className="glass p-2 rounded-lg hover:bg-surface-sunken/70 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <PiMinusLight className="h-4 w-4 text-fg-muted" />
                    </button>
                    <span className="text-2xl font-medium text-fg-strong w-12 text-center">{newQuantity}</span>
                    <button
                      onClick={() => setNewQuantity(newQuantity + 1)}
                      className="glass p-2 rounded-lg hover:bg-surface-sunken/70 transition-colors"
                    >
                      <PiPlusLight className="h-4 w-4 text-fg-muted" />
                    </button>
                  </div>
                </div>

                {/* Delta info + cost */}
                {delta !== 0 && (
                  <div className={`rounded-lg border p-3 text-xs ${
                    belowUsed
                      ? "border-danger-200 dark:border-danger-800 bg-danger-50 dark:bg-danger-900/20 text-danger-600 dark:text-danger-400"
                      : delta > 0
                      ? "border-info-200 dark:border-info-800 bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300"
                      : "border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 text-warning-700 dark:text-warning-300"
                  }`}>
                    {belowUsed
                      ? `${seatsUsed} seat(s) are currently in use. Reducing below this may remove access for some users.`
                      : delta > 0
                      ? `Adding ${delta} seat(s). You will be charged immediately for the prorated amount.`
                      : `Removing ${Math.abs(delta)} seat(s). A credit will be applied to your account.`
                    }
                    {perSeatPrice != null && delta > 0 && !belowUsed && (
                      <p className="mt-1 font-medium">
                        {delta} × {formatPrice(perSeatPrice)}/{t("mo")} = +{formatPrice(perSeatPrice * delta)}/{t("mo")}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Members section */}
              {members.length > 0 && (
                <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                  <div className="px-5 pt-4 pb-2">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-medium text-fg-strong">
                        {t("Team members")}
                        <span className="text-fg-muted font-normal ms-1">({members.length})</span>
                      </p>
                    </div>
                    {members.length > 6 && (
                      <div className="mb-2">
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder={t("Search members...")}
                        />
                      </div>
                    )}
                  </div>
                  <div className="px-5 pb-3 overflow-y-auto flex-1 min-h-0">
                    <div className="space-y-1">
                      {filteredMembers.map((member) => (
                        <div
                          key={member.id}
                          className="flex items-center gap-3 p-2.5 rounded-lg border border-transparent hover:bg-surface-sunken/70"
                        >
                          <ProfileImage
                            profilePicture={normalise(member).avatar}
                            email={member.email}
                            initials={
                              member.name
                                ? member.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .toUpperCase()
                                    .slice(0, 2)
                                : (member.email?.[0] || "?").toUpperCase()
                            }
                            name={member.name}
                            size="h-8 w-8"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-fg-strong truncate">
                              {member.name || member.email}
                            </p>
                            {member.name && (
                              <p className="text-xs text-fg-muted truncate">
                                {member.email}
                              </p>
                            )}
                          </div>
                          {member.role && (
                            <Badge color="gray" className="flex-shrink-0">
                              {formatRole(member.role)}
                            </Badge>
                          )}
                          <Badge color="green" className="flex-shrink-0">
                            {t("Seated")}
                          </Badge>
                        </div>
                      ))}
                    </div>
                    {filteredMembers.length === 0 && members.length > 0 && (
                      <p className="text-sm text-fg-muted text-center py-4">
                        {t("No members match your search")}
                      </p>
                    )}
                  </div>
                </div>
              )}

    </ModalShell>
  );
};

export default AddSeatsModal;
